import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2, X } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { listarUsuarios } from '../../usuarios/api';
import type { ScopeChange, SignatoryInput } from '../api';

function useUsuarioOptions() {
  const { data, isLoading } = useQuery({
    queryKey: ['picker', 'usuarios'],
    queryFn: () => listarUsuarios({ limit: 200 }),
    staleTime: 60 * 1000,
  });
  const options = useMemo(
    () => (data?.data ?? []).map((u) => ({ value: u.id, label: u.name, description: u.email })),
    [data],
  );
  return { options, isLoading };
}

interface ModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  onConfirm: () => void;
  isSubmitting?: boolean;
  confirmLabel?: string;
  confirmVariant?: 'primary' | 'danger';
}

/** Mesmo shell de `PropostaActionModals.tsx` — `bg-black/50`, `rounded-lg`, header com borda, ESC fecha. */
function ModalShell({
  isOpen,
  onClose,
  title,
  children,
  onConfirm,
  isSubmitting,
  confirmLabel = 'Confirmar',
  confirmVariant = 'primary',
}: ModalShellProps) {
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isSubmitting) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, isSubmitting]);

  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="action-modal-title"
      onClick={isSubmitting ? undefined : onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 p-4 shrink-0">
          <h3 id="action-modal-title" className="text-lg font-semibold text-gray-900">{title}</h3>
          <button type="button" onClick={onClose} aria-label="Fechar" className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto">{children}</div>
        <div className="flex justify-end gap-2 border-t border-gray-200 p-4 shrink-0">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button variant={confirmVariant} onClick={onConfirm} loading={isSubmitting}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---- SuspendContractModal ----

interface SuspendData { reason: string; resumptionDate?: string }
interface SuspendProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: SuspendData) => void;
  isSubmitting?: boolean;
}

export function SuspendContractModal({ isOpen, onClose, onConfirm, isSubmitting }: SuspendProps) {
  const [reason, setReason] = useState('');
  const [resumptionDate, setResumptionDate] = useState('');
  const [err, setErr] = useState('');

  const handleConfirm = () => {
    if (!reason.trim()) { setErr('Motivo é obrigatório.'); return; }
    setErr('');
    onConfirm({ reason, resumptionDate: resumptionDate || undefined });
  };

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} title="Suspender Contrato" onConfirm={handleConfirm} isSubmitting={isSubmitting} confirmLabel="Suspender">
      <Textarea label="Motivo *" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} error={err} />
      <TextInput label="Previsão de Retomada" type="date" value={resumptionDate} onChange={(e) => setResumptionDate(e.target.value)} />
    </ModalShell>
  );
}

// ---- RescindContractModal ----

interface RescindData { motivo: string; dataEfetiva: string; responsavelId: string; documentoComprobatorio: string; observacoes?: string }
interface RescindProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: RescindData) => void;
  isSubmitting?: boolean;
}

export function RescindContractModal({ isOpen, onClose, onConfirm, isSubmitting }: RescindProps) {
  const [form, setForm] = useState<RescindData>({ motivo: '', dataEfetiva: '', responsavelId: '', documentoComprobatorio: '' });
  const [errs, setErrs] = useState<Partial<Record<keyof RescindData, string>>>({});
  const { options: usuarioOptions, isLoading: isLoadingUsuarios } = useUsuarioOptions();

  const set = (k: keyof RescindData, v: string) => { setForm((p) => ({ ...p, [k]: v })); setErrs((p) => ({ ...p, [k]: undefined })); };

  const handleConfirm = () => {
    const e: typeof errs = {};
    if (!form.motivo.trim()) e.motivo = 'Obrigatório.';
    if (!form.dataEfetiva) e.dataEfetiva = 'Obrigatório.';
    if (!form.responsavelId.trim()) e.responsavelId = 'Obrigatório.';
    if (!form.documentoComprobatorio.trim()) e.documentoComprobatorio = 'Obrigatório.';
    if (Object.keys(e).length > 0) { setErrs(e); return; }
    onConfirm(form);
  };

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} title="Rescindir Contrato" onConfirm={handleConfirm} isSubmitting={isSubmitting} confirmLabel="Rescindir" confirmVariant="danger">
      <Textarea label="Motivo *" value={form.motivo} onChange={(e) => set('motivo', e.target.value)} rows={2} error={errs.motivo} />
      <TextInput label="Data Efetiva *" type="date" value={form.dataEfetiva} onChange={(e) => set('dataEfetiva', e.target.value)} error={errs.dataEfetiva} />
      <AutocompleteSelect
        label={isLoadingUsuarios ? 'Carregando…' : 'Responsável *'}
        options={usuarioOptions}
        value={form.responsavelId}
        onChange={(v) => set('responsavelId', v)}
        error={errs.responsavelId}
        disabled={isLoadingUsuarios}
      />
      <TextInput
        label="Documento Comprobatório *"
        value={form.documentoComprobatorio}
        onChange={(e) => set('documentoComprobatorio', e.target.value)}
        error={errs.documentoComprobatorio}
      />
      <Textarea label="Observações" value={form.observacoes ?? ''} onChange={(e) => set('observacoes', e.target.value)} rows={2} />
    </ModalShell>
  );
}

// ---- CloseContractModal ----

interface CloseData { reason: string; observacoes?: string }
interface CloseProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: CloseData) => void;
  isSubmitting?: boolean;
}

export function CloseContractModal({ isOpen, onClose, onConfirm, isSubmitting }: CloseProps) {
  const [reason, setReason] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [err, setErr] = useState('');

  const handleConfirm = () => {
    if (!reason.trim()) { setErr('Motivo é obrigatório.'); return; }
    setErr('');
    onConfirm({ reason, observacoes: observacoes || undefined });
  };

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} title="Encerrar Contrato" onConfirm={handleConfirm} isSubmitting={isSubmitting} confirmLabel="Encerrar">
      <Textarea label="Motivo *" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} error={err} />
      <Textarea label="Observações" value={observacoes} onChange={(e) => setObservacoes(e.target.value)} rows={2} />
    </ModalShell>
  );
}

// ---- RenewContractModal ----

export interface RenewData {
  renewalMode: 'mesmo_contrato' | 'novo_contrato_vinculado';
  novaVigenciaFinal: string;
  novaVigenciaInicial?: string;
  reason?: string;
}
interface RenewProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: RenewData) => void;
  isSubmitting?: boolean;
  /** Vigência final atual do contrato — sugerida como início do novo período. */
  vigenciaFinalAtual: string;
}

const RENEWAL_MODE_OPTIONS = [
  { value: 'mesmo_contrato', label: 'Manter o mesmo contrato com novo período' },
  { value: 'novo_contrato_vinculado', label: 'Gerar novo contrato vinculado ao atual' },
];

export function RenewContractModal({ isOpen, onClose, onConfirm, isSubmitting, vigenciaFinalAtual }: RenewProps) {
  const [renewalMode, setRenewalMode] = useState<RenewData['renewalMode']>('mesmo_contrato');
  const [novaVigenciaFinal, setNovaVigenciaFinal] = useState('');
  const [novaVigenciaInicial, setNovaVigenciaInicial] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');

  const handleConfirm = () => {
    if (!novaVigenciaFinal) { setErr('Nova vigência final é obrigatória.'); return; }
    setErr('');
    onConfirm({
      renewalMode,
      novaVigenciaFinal,
      novaVigenciaInicial: novaVigenciaInicial || undefined,
      reason: reason || undefined,
    });
  };

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} title="Renovar Contrato" onConfirm={handleConfirm} isSubmitting={isSubmitting} confirmLabel="Renovar">
      <AutocompleteSelect
        label="Modo de renovação *"
        options={RENEWAL_MODE_OPTIONS}
        value={renewalMode}
        onChange={(v) => setRenewalMode(v as RenewData['renewalMode'])}
      />
      <div>
        <TextInput label="Nova vigência inicial" type="date" value={novaVigenciaInicial} onChange={(e) => setNovaVigenciaInicial(e.target.value)} />
        <p className="mt-1 text-xs text-gray-500">Se vazio, usa {vigenciaFinalAtual} (fim da vigência atual).</p>
      </div>
      <TextInput label="Nova vigência final *" type="date" value={novaVigenciaFinal} onChange={(e) => setNovaVigenciaFinal(e.target.value)} error={err} />
      <Textarea label="Observações" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
    </ModalShell>
  );
}

// ---- CreateAddendumModal ----

const SCOPE_CHANGE_TYPES: { value: ScopeChange['type']; label: string }[] = [
  { value: 'escopo', label: 'Escopo' },
  { value: 'vigencia', label: 'Vigência' },
  { value: 'valor', label: 'Valores' },
  { value: 'reajuste', label: 'Reajustes' },
  { value: 'clausulas', label: 'Cláusulas' },
  { value: 'quantitativos', label: 'Quantitativos' },
  { value: 'anexos', label: 'Anexos' },
];

export interface AddendumData {
  justificativa: string;
  scopeChanges: ScopeChange[];
  approverUserIds: string[];
  vigenciaInicial?: string;
}
interface AddendumProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: AddendumData) => void;
  isSubmitting?: boolean;
}

export function CreateAddendumModal({ isOpen, onClose, onConfirm, isSubmitting }: AddendumProps) {
  const [justificativa, setJustificativa] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<Record<string, string>>({});
  const [approverIds, setApproverIds] = useState<string[]>([]);
  const [pickedApprover, setPickedApprover] = useState('');
  const [vigenciaInicial, setVigenciaInicial] = useState('');
  const [err, setErr] = useState('');
  const { options: usuarioOptions, isLoading: isLoadingUsuarios } = useUsuarioOptions();

  const toggleType = (type: string) => {
    setSelectedTypes((prev) => {
      const next = { ...prev };
      if (type in next) delete next[type];
      else next[type] = '';
      return next;
    });
  };

  const handleConfirm = () => {
    if (justificativa.trim().length < 20) { setErr('Justificativa deve ter ao menos 20 caracteres.'); return; }
    const types = Object.keys(selectedTypes);
    if (types.length === 0) { setErr('Selecione ao menos um aspecto de escopo alterado.'); return; }
    if (approverIds.length === 0) { setErr('Selecione ao menos um aprovador.'); return; }
    setErr('');
    onConfirm({
      justificativa,
      scopeChanges: types.map((type) => ({
        type: type as ScopeChange['type'],
        descricao: selectedTypes[type] || undefined,
      })),
      approverUserIds: approverIds,
      vigenciaInicial: vigenciaInicial || undefined,
    });
  };

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} title="Criar Aditivo" onConfirm={handleConfirm} isSubmitting={isSubmitting} confirmLabel="Criar Aditivo">
      {err && <Alert variant="error">{err}</Alert>}
      <Textarea
        label="Justificativa *"
        value={justificativa}
        onChange={(e) => setJustificativa(e.target.value)}
        rows={3}
        showCharCount
        minLength={20}
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">O que este aditivo altera? *</label>
        <div className="space-y-2">
          {SCOPE_CHANGE_TYPES.map((t) => (
            <div key={t.value}>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={t.value in selectedTypes}
                  onChange={() => toggleType(t.value)}
                />
                {t.label}
              </label>
              {t.value in selectedTypes && (
                <input
                  type="text"
                  value={selectedTypes[t.value]}
                  onChange={(e) => setSelectedTypes((prev) => ({ ...prev, [t.value]: e.target.value }))}
                  placeholder="Descrição da alteração (opcional)"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            </div>
          ))}
        </div>
      </div>
      <TextInput label="Nova vigência inicial do aditivo" type="date" value={vigenciaInicial} onChange={(e) => setVigenciaInicial(e.target.value)} />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Aprovadores *</label>
        <div className="flex gap-2 items-start">
          <div className="flex-1">
            <AutocompleteSelect
              label="Adicionar aprovador"
              options={usuarioOptions.filter((o) => !approverIds.includes(o.value))}
              value={pickedApprover}
              onChange={setPickedApprover}
              disabled={isLoadingUsuarios}
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => {
              if (pickedApprover && !approverIds.includes(pickedApprover)) {
                setApproverIds((prev) => [...prev, pickedApprover]);
                setPickedApprover('');
              }
            }}
          />
        </div>
        {approverIds.length > 0 && (
          <ul className="mt-2 space-y-1">
            {approverIds.map((id) => {
              const u = usuarioOptions.find((o) => o.value === id);
              return (
                <li key={id} className="flex items-center justify-between text-sm bg-gray-50 rounded px-2 py-1">
                  <span>{u?.label ?? id}</span>
                  <button type="button" onClick={() => setApproverIds((prev) => prev.filter((a) => a !== id))} className="text-gray-400 hover:text-red-600">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </ModalShell>
  );
}

// ---- ConfirmTransitionModal ----
// Transições simples do ciclo de vida (enviar para revisão jurídica,
// enviar para aprovação, aprovar, devolver, ativar) — sem campos
// próprios, só confirmação e motivo opcional.

interface ConfirmTransitionProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
  isSubmitting?: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  requireReason?: boolean;
}

export function ConfirmTransitionModal({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
  title,
  description,
  confirmLabel,
  requireReason,
}: ConfirmTransitionProps) {
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');

  const handleConfirm = () => {
    if (requireReason && !reason.trim()) { setErr('Motivo é obrigatório.'); return; }
    setErr('');
    onConfirm(reason.trim() || undefined);
    setReason('');
  };

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} title={title} onConfirm={handleConfirm} isSubmitting={isSubmitting} confirmLabel={confirmLabel}>
      <p className="text-sm text-gray-600">{description}</p>
      <Textarea label={`Motivo${requireReason ? ' *' : ' (opcional)'}`} value={reason} onChange={(e) => setReason(e.target.value)} rows={2} error={err} />
    </ModalShell>
  );
}

// ---- CancelContractModal ----

interface CancelProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isSubmitting?: boolean;
}

export function CancelContractModal({ isOpen, onClose, onConfirm, isSubmitting }: CancelProps) {
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');

  const handleConfirm = () => {
    if (!reason.trim()) { setErr('Motivo é obrigatório.'); return; }
    setErr('');
    onConfirm(reason);
  };

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} title="Cancelar Contrato" onConfirm={handleConfirm} isSubmitting={isSubmitting} confirmLabel="Cancelar Contrato" confirmVariant="danger">
      <Textarea label="Motivo *" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} error={err} />
    </ModalShell>
  );
}

// ---- Assinatura (enviar / registrar manual) ----

interface SignatoryRow extends SignatoryInput {
  key: string;
}

interface SignatoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (signatories: SignatoryInput[]) => void;
  isSubmitting?: boolean;
  title: string;
  description: string;
  confirmLabel: string;
}

function emptyRow(): SignatoryRow {
  return { key: crypto.randomUUID(), name: '', email: '', userId: undefined };
}

function SignatoriesModal({ isOpen, onClose, onConfirm, isSubmitting, title, description, confirmLabel }: SignatoriesModalProps) {
  const [rows, setRows] = useState<SignatoryRow[]>([emptyRow()]);
  const [err, setErr] = useState('');
  const { options: usuarioOptions, isLoading: isLoadingUsuarios } = useUsuarioOptions();

  const setRow = (key: string, patch: Partial<SignatoryRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const handleUserPick = (key: string, userId: string) => {
    const u = usuarioOptions.find((o) => o.value === userId);
    setRow(key, { userId: userId || undefined, name: u?.label ?? '', email: u?.description ?? '' });
  };

  const handleConfirm = () => {
    const valid = rows.filter((r) => r.name.trim() && r.email.trim());
    if (valid.length === 0) { setErr('Informe ao menos um signatário com nome e e-mail.'); return; }
    setErr('');
    onConfirm(valid.map(({ key: _key, ...rest }) => rest));
  };

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} title={title} onConfirm={handleConfirm} isSubmitting={isSubmitting} confirmLabel={confirmLabel}>
      <p className="text-sm text-gray-600">{description}</p>
      {err && <Alert variant="error">{err}</Alert>}
      <div className="space-y-3">
        {rows.map((row, idx) => (
          <div key={row.key} className="border border-gray-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500">Signatário {idx + 1}</span>
              {rows.length > 1 && (
                <button type="button" onClick={() => setRows((prev) => prev.filter((r) => r.key !== row.key))} className="text-gray-400 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <AutocompleteSelect
              label={isLoadingUsuarios ? 'Carregando…' : 'Vincular a usuário do sistema (opcional)'}
              options={usuarioOptions}
              value={row.userId ?? ''}
              onChange={(v) => handleUserPick(row.key, v)}
              disabled={isLoadingUsuarios}
            />
            <TextInput label="Nome" value={row.name} onChange={(e) => setRow(row.key, { name: e.target.value })} />
            <TextInput label="E-mail" type="email" value={row.email} onChange={(e) => setRow(row.key, { email: e.target.value })} />
          </div>
        ))}
      </div>
      <Button type="button" variant="ghost" size="sm" icon={<Plus className="w-4 h-4" />} onClick={() => setRows((prev) => [...prev, emptyRow()])}>
        Adicionar signatário
      </Button>
    </ModalShell>
  );
}

type SendForSignatureModalProps = Omit<SignatoriesModalProps, 'title' | 'description' | 'confirmLabel'>;

export function SendForSignatureModal(props: SendForSignatureModalProps) {
  return (
    <SignatoriesModal
      {...props}
      title="Enviar para Assinatura Eletrônica"
      description="Informe os signatários que receberão o contrato para assinatura eletrônica."
      confirmLabel="Enviar"
    />
  );
}

export function RegisterManualSignatureModal(props: SendForSignatureModalProps) {
  return (
    <SignatoriesModal
      {...props}
      title="Registrar Assinatura Presencial"
      description="Registre os signatários que já assinaram fisicamente. O contrato será marcado como Assinado."
      confirmLabel="Registrar"
    />
  );
}
